import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { filterGlobalSearch } from '../utils/globalSearchIndex';
import { splitHighlight } from '../utils/searchHighlight';

function Highlighted({ text, query, className = '' }) {
  const parts = useMemo(() => splitHighlight(text, query), [text, query]);
  return (
    <span className={className}>
      {parts.map((p) =>
        p.mark ? (
          <mark
            key={p.key}
            className="rounded-sm bg-amber-200 px-0.5 text-inherit dark:bg-amber-600/50 dark:text-amber-50"
          >
            {p.text}
          </mark>
        ) : (
          <span key={p.key}>{p.text}</span>
        ),
      )}
    </span>
  );
}

const SearchPage = () => {
  const { isDarkMode } = useTheme();
  const { user, isSuperAdmin } = useAuth();
  const features = user?.subscription_plan_features || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const qRaw = searchParams.get('q') || '';
  const q = qRaw.trim();
  const context = searchParams.get('context') === 'ev' ? 'ev' : 'default';
  const [inputValue, setInputValue] = useState(qRaw);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(qRaw);
  }, [qRaw]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo(
    () => (q ? filterGlobalSearch(q, features, isSuperAdmin, { limit: 40, context, user }) : []),
    [q, features, isSuperAdmin, context, user],
  );

  const onSubmit = (e) => {
    e.preventDefault();
    const next = inputValue.trim();
    if (next) {
      const params = { q: next };
      if (context === 'ev') params.context = 'ev';
      setSearchParams(params);
    } else {
      setSearchParams(context === 'ev' ? { context: 'ev' } : {});
    }
  };

  const card = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const heading = isDarkMode ? 'text-white' : 'text-gray-900';

  return (
    <div className={`min-h-screen w-full px-4 py-8 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-3xl mx-auto">
        <h1 className={`text-2xl font-bold mb-2 ${heading}`}>Search</h1>
        <p className={`text-sm mb-6 ${muted}`}>
          Find pages across Stratosphere. Enter a search term and press <kbd className="px-1.5 py-0.5 rounded border text-xs font-mono">Enter</kbd> to see matching pages with highlights.
        </p>

        <form onSubmit={onSubmit} className="mb-8">
          <div
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 shadow-sm ${
              isDarkMode ? 'border-gray-600 bg-gray-800/80' : 'border-gray-200 bg-white'
            }`}
          >
            <Search className={`h-5 w-5 flex-shrink-0 ${muted}`} />
            <input
              ref={inputRef}
              type="search"
              name="q"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search pages, topics, and tools…"
              autoComplete="off"
              className={`flex-1 min-w-0 bg-transparent text-base outline-none ${
                isDarkMode ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-400'
              }`}
              aria-label="Search query"
            />
            <button
              type="submit"
              className="flex-shrink-0 rounded-lg px-4 py-2 text-sm font-medium btn-primary-theme"
            >
              Search
            </button>
          </div>
        </form>

        {!q ? (
          <div
            className={`rounded-xl border p-8 text-center ${card}`}
          >
            <p className={`text-sm ${muted}`}>
              Enter a search term above and press <kbd className="px-1.5 py-0.5 rounded border text-xs font-mono">Enter</kbd> or click{' '}
              <strong className={heading}>Search</strong> to see matching pages with highlights.
            </p>
          </div>
        ) : (
          <>
            <div className={`mb-4 text-sm ${muted}`}>
              <span className={heading}>Results for: </span>
              <Highlighted text={q} query={q} className="font-medium text-primary-600 dark:text-primary-300" />
              <span className="mx-2">·</span>
              <span>
                {results.length} page{results.length !== 1 ? 's' : ''} found
              </span>
            </div>

            {results.length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${card}`}>
                <p className={`text-sm ${muted}`}>No pages matched your search. Try different or fewer keywords.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {results.map((item) => (
                  <li key={`${item.path}-${item.title}`}>
                    <Link
                      to={item.path}
                      className={`block rounded-xl border p-4 transition-shadow hover:shadow-md ${card} ${
                        isDarkMode ? 'hover:border-primary-500/40' : 'hover:border-primary-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className={`text-lg font-semibold leading-snug mb-1 ${heading}`}>
                            <Highlighted text={item.title} query={q} />
                          </p>
                          <p className={`text-xs uppercase tracking-wide mb-2 ${muted}`}>
                            <span className="font-medium text-primary-600 dark:text-primary-400">Page</span>
                            <span className="mx-2 opacity-50">|</span>
                            <Highlighted text={item.category} query={q} />
                          </p>
                          {item.description ? (
                            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <Highlighted text={item.description} query={q} />
                            </p>
                          ) : null}
                          {Array.isArray(item.sections) && item.sections.length > 0 ? (
                            <div className={`mt-3 text-xs ${muted}`}>
                              <span className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Sections on this page:{' '}
                              </span>
                              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                {item.sections.map((line) => line.split('|')[0].trim()).join(' · ')}
                              </span>
                            </div>
                          ) : null}
                          <p className={`text-xs font-mono mt-2 ${muted}`}>
                            <Highlighted text={item.path} query={q} />
                          </p>
                        </div>
                        <ArrowRight className={`h-5 w-5 flex-shrink-0 mt-1 ${muted}`} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
